const DATABASE_NAME = "neptune-resource-drafts";
const DATABASE_VERSION = 1;
const STORE_NAME = "drafts";
const FALLBACK_PREFIX = "neptune.resource-draft.";

const canUseIndexedDb = () =>
  typeof window !== "undefined" && "indexedDB" in window;

const openDatabase = () =>
  new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const useStore = async (mode, callback) => {
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = callback(transaction.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
};

const fallbackKey = (key) => `${FALLBACK_PREFIX}${key}`;

export const getResourceDraft = async (key) => {
  if (!key) return null;

  if (canUseIndexedDb()) {
    try {
      return (await useStore("readonly", (store) => store.get(key))) ?? null;
    } catch (error) {
      console.warn("Unable to read resource draft from IndexedDB.", error);
    }
  }

  try {
    const value = window.localStorage.getItem(fallbackKey(key));
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

export const saveResourceDraft = async (key, draft) => {
  if (!key || !draft) return;
  const record = { ...draft, key };

  if (canUseIndexedDb()) {
    try {
      await useStore("readwrite", (store) => store.put(record));
      return;
    } catch (error) {
      console.warn("Unable to save resource draft to IndexedDB.", error);
    }
  }

  try {
    window.localStorage.setItem(fallbackKey(key), JSON.stringify(record));
  } catch (error) {
    console.warn("Unable to save resource draft in browser storage.", error);
  }
};

export const deleteResourceDraft = async (key) => {
  if (!key) return;

  if (canUseIndexedDb()) {
    try {
      await useStore("readwrite", (store) => store.delete(key));
    } catch (error) {
      console.warn("Unable to delete resource draft from IndexedDB.", error);
    }
  }

  try {
    window.localStorage.removeItem(fallbackKey(key));
  } catch {
    // Storage can be unavailable in hardened browser modes.
  }
};
